import { useCallback } from 'react';
import { Employee, Company } from '../types';
import { countEmployeeAbsencesInMonth, groupAbsencesByMonth } from '../utils/absences';
import { sortEmployeesByName } from '../utils/employeeSort';
import { formatLocalDate } from '../utils/dateFormat';

export interface UsePdfExportParams {
  items: Record<string, Employee[]>;
  columns: { id: string; title: string; color?: string }[];
  currentCompany: Company | null;
  onReportGenerated: (title: string, message: string) => void;
  /** Data de referência para métricas mensais no relatório de faltas. */
  referenceDate?: Date;
}

/** Carrega jsPDF sob demanda — remove ~360KB do bundle inicial. */
async function createPdfDocument() {
  const { jsPDF } = await import('jspdf');
  return new jsPDF();
}

type PdfDoc = Awaited<ReturnType<typeof createPdfDocument>>;

/**
 * Gera relatórios PDF de colaboradores e faltas (extraído de App.tsx para testabilidade).
 */
export function usePdfExport({
  items,
  columns,
  currentCompany,
  onReportGenerated,
  referenceDate = new Date(),
}: UsePdfExportParams) {
  const buildPdfHeader = useCallback((doc: PdfDoc, title: string, subtitle: string) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFillColor(37, 99, 235);
    doc.rect(0, 0, pageWidth, 34, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(title, 14, 14);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(subtitle, 14, 20);
    doc.text(`Emissão: ${new Date().toLocaleString('pt-BR')}`, 14, 26);
    doc.text(`Empresa: ${currentCompany || 'N/A'}`, 14, 31);
    doc.setTextColor(30, 41, 59);
  }, [currentCompany]);

  const drawSectionTitle = useCallback((doc: PdfDoc, title: string, y: number) => {
    doc.setFillColor(241, 245, 249);
    doc.roundedRect(14, y - 4, 182, 8, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(title, 16, y + 1);
  }, []);

  const drawPageFooter = useCallback((doc: PdfDoc) => {
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setTextColor(100, 116, 139);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(`Página ${i} de ${totalPages}`, 170, 292);
    }
    doc.setTextColor(30, 41, 59);
  }, []);

  const handleDownloadEmployeesReport = useCallback(async () => {
    const allEmployees = sortEmployeesByName(Object.values(items).flat() as Employee[]);
    const doc = await createPdfDocument();
    buildPdfHeader(doc, 'Relatório de Colaboradores', 'Visão geral dos colaboradores cadastrados');

    let y = 42;
    drawSectionTitle(doc, 'Resumo', y);
    y += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Total de colaboradores: ${allEmployees.length}`, 16, y);
    y += 8;

    drawSectionTitle(doc, 'Detalhamento', y);
    y += 9;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('#', 16, y);
    doc.text('Nome', 24, y);
    doc.text('Cargo', 92, y);
    doc.text('Setor', 145, y);
    y += 4;
    doc.setDrawColor(226, 232, 240);
    doc.line(14, y, 196, y);
    y += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    allEmployees.forEach((emp, index) => {
      const colId = Object.keys(items).find((key) => items[key].some((e) => e.id === emp.id));
      const setor = columns.find((c) => c.id === colId)?.title || 'N/A';

      if (y > 282) {
        doc.addPage();
        y = 18;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text('#', 16, y);
        doc.text('Nome', 24, y);
        doc.text('Cargo', 92, y);
        doc.text('Setor', 145, y);
        y += 4;
        doc.line(14, y, 196, y);
        y += 5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
      }

      const name = doc.splitTextToSize(emp.name || '-', 64);
      const role = doc.splitTextToSize(emp.role || '-', 50);
      const dept = doc.splitTextToSize(setor, 48);
      const rowHeight = Math.max(name.length, role.length, dept.length) * 4;
      doc.text(String(index + 1), 16, y);
      doc.text(name, 24, y);
      doc.text(role, 92, y);
      doc.text(dept, 145, y);
      y += rowHeight + 2;
      doc.setDrawColor(241, 245, 249);
      doc.line(14, y - 1, 196, y - 1);
    });

    drawPageFooter(doc);
    doc.save(`relatorio-colaboradores-${new Date().toISOString().slice(0, 10)}.pdf`);
    onReportGenerated('Relatório gerado', 'Relatório de Colaboradores baixado em PDF.');
  }, [items, columns, buildPdfHeader, drawSectionTitle, drawPageFooter, onReportGenerated]);

  const handleDownloadAbsencesReport = useCallback(async () => {
    const allEmployees = Object.values(items).flat() as Employee[];
    const employeesWithAbsences = sortEmployeesByName(
      allEmployees.filter((emp) => (emp.absences?.length || 0) > 0),
    );
    const doc = await createPdfDocument();
    buildPdfHeader(doc, 'Relatório de Faltas', 'Consolidação das ausências por colaborador');

    let y = 42;
    doc.setFontSize(10);
    const totalAbsences = employeesWithAbsences.reduce(
      (acc, emp) => acc + (emp.absences?.length || 0),
      0,
    );
    const absencesThisMonth = employeesWithAbsences.reduce(
      (acc, emp) => acc + countEmployeeAbsencesInMonth(emp, referenceDate),
      0,
    );

    drawSectionTitle(doc, 'Resumo', y);
    y += 8;
    doc.setFont('helvetica', 'normal');
    doc.text(`Colaboradores com faltas: ${employeesWithAbsences.length}`, 16, y);
    y += 5;
    doc.text(`Faltas no mês atual: ${absencesThisMonth}`, 16, y);
    y += 5;
    doc.text(`Total histórico de faltas: ${totalAbsences}`, 16, y);
    y += 8;
    drawSectionTitle(doc, 'Detalhamento por colaborador', y);
    y += 9;

    if (employeesWithAbsences.length === 0) {
      doc.text('Nenhuma falta registrada no momento.', 16, y);
    } else {
      employeesWithAbsences.forEach((emp) => {
        if (y > 270) {
          doc.addPage();
          y = 18;
        }

        const colId = Object.keys(items).find((key) => items[key].some((e) => e.id === emp.id));
        const setor = columns.find((c) => c.id === colId)?.title || 'N/A';
        doc.setFillColor(248, 250, 252);
        doc.roundedRect(14, y - 4, 182, 8, 2, 2, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.text(`${emp.name}`, 16, y + 1);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        const monthCount = countEmployeeAbsencesInMonth(emp, referenceDate);
        doc.text(
          `Setor: ${setor} | Mês: ${monthCount} | Total: ${emp.absences?.length || 0}`,
          140,
          y + 1,
          { align: 'right' },
        );
        y += 9;

        groupAbsencesByMonth(emp.absences ?? []).forEach((group) => {
          if (y > 282) {
            doc.addPage();
            y = 18;
          }
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.setTextColor(100, 116, 139);
          doc.text(group.label, 18, y);
          doc.setTextColor(0, 0, 0);
          y += 5;

          group.absences.forEach((absence, index) => {
            if (y > 282) {
              doc.addPage();
              y = 18;
            }
            const dataFmt = formatLocalDate(absence.date);
            const reasonLines = doc.splitTextToSize(absence.reason || '-', 145);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8.5);
            doc.text(`${index + 1}. ${dataFmt}`, 22, y);
            doc.setFont('helvetica', 'normal');
            doc.text(reasonLines, 52, y);
            y += Math.max(5, reasonLines.length * 4 + 1);
          });
          y += 2;
        });
        y += 3;
      });
    }

    drawPageFooter(doc);
    doc.save(`relatorio-faltas-${new Date().toISOString().slice(0, 10)}.pdf`);
    onReportGenerated('Relatório gerado', 'Relatório de Faltas baixado em PDF.');
  }, [items, columns, referenceDate, buildPdfHeader, drawSectionTitle, drawPageFooter, onReportGenerated]);

  return {
    handleDownloadEmployeesReport,
    handleDownloadAbsencesReport,
  };
}
